import React, { useState, useRef } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Query, QueryResult, Column } from 'material-table';
import {
	Grid,
	makeStyles,
	createStyles,
	FormControl,
	FormControlLabel,
	Radio,
	RadioGroup,
	TextFieldProps,
	TextField,
	InputAdornment,
	Select,
	MenuItem,
} from '@material-ui/core';
import { IOverrideEntityActions } from '../../../API/Interfaces/IEntityActions';
import ISearchBaseDto from '../../../API/Interfaces/ISearchBaseDto';
import EntityPage from '../../../Layout/EntityPage';
import { CategoryButtons } from './CategoryButtons';
import ReportServiceAPI from '../../../API/Services/ReportServiceAPI';
import { useParams } from 'react-router-dom';
import { IGenericDonationReportModel, IDonationReportDetailsModel } from '../../../API/Interfaces/IReportDtos';
import moment from 'moment';
import {
	yearMonthDayFormat,
	DictReportType,
	DictReportTypeLabel,
	locale,
	dateFormatWithDots,
	formatIsoDateString,
} from '../../../Constants/constants';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { ArrowDownIcon } from '../../../Layout/Components/Icons';

interface IConfigurationProps {
	isInDetails?: boolean;
	locationId?: string;
}

interface IFiltersReportsDto extends IGenericDonationReportModel {
	reportType: string;
	dateMonthly: Date | string;
	period?: string;
}

const useStyles = makeStyles(() =>
	createStyles({
		filterLabel: {
			opacity: '0.9',
			fontWeight: 500,
			fontSize: '18px',
			lineHeight: '21px',
			marginRight: '15px',
		},
	})
);

export const LocationReports = (props: IConfigurationProps) => {
	const { t } = useTranslation();
	const tableRef = useRef();
	const { enqueueSnackbar } = useSnackbar();
	const errorDialog = (message?: string) =>
		enqueueSnackbar(`${t('common.errorOccured')}${message ? ': ' : ''}${message && message}`, { variant: 'error' });
	const { id } = useParams<{ id: string }>();

	const donationsListLabel: string = `${t('report.DonationReport')}`;
	const vouchersListLabel: string = `${t('report.voucherReport')}`;
	const [selectedReport, setSelectedReport] = useState(donationsListLabel);
	const classes = useStyles();
	let headers;

	const defaultFilterValues: IFiltersReportsDto = {
		reportType: DictReportType.RealTime,
		locationId: id,
		startTime: new Date(moment().add(-6, 'days').toString()),
		endTime: new Date(moment().format(dateFormatWithDots)),
		dateMonthly: new Date(moment().add(-1, 'months').toString()),
	};
	const [filters, setFilters] = useState<IFiltersReportsDto>(defaultFilterValues);

	const headerInfo: Array<Column<IDonationReportDetailsModel>> = [
		{
			title:
				filters.reportType === DictReportType.Monthly
					? t('component.paymentHistoryTable.paymentMonth')
					: t('component.paymentHistoryTable.paymentDate'),
			field: 'date',
			render: (rowData) => <span>{formatIsoDateString(rowData.date)}</span>,
		},
		{
			title: t('component.paymentHistoryTable.amount') + ' (PLN)',
			field: 'amount',
			render: (rowData) => <span>{(parseFloat(rowData.amount?.toString() || '') / 100).toFixed(2)}</span>,
		},
		{
			title: t('report.Table.TargetHeader'),
			field: 'goalName',
		},
		{
			title: t('component.paymentHistoryTable.additionalInfo'),
			field: 'additionalInfo',
			render: (rowData) => styleAdditionalInfo(rowData.additionalInfo),
		},
		{
			title: t('component.paymentHistoryTable.total'),
			field: 'total',
			sorting: false,
			render: (rowData) => <span>{(parseFloat(rowData.total?.toString() || '') / 100).toFixed(2)}</span>,
		},
	];

	const styleAdditionalInfo = (text: string) => {
		if (text && text.includes(':')) {
			const splittedText = text.split(':');
			return (
				<span>
					{splittedText[0]}: <span style={{ fontWeight: 'bold' }}>{splittedText[1]}</span>
				</span>
			);
		} else return text;
	};

	if (filters.reportType === DictReportType.RealTime) {
		headers = headerInfo.filter((t) => t.field !== 'total');
	} else {
		headers = headerInfo.filter((t) => t.field !== 'amount' && t.field !== 'additionalInfo');
	}

	const entityTitleButtons = () => (
		<CategoryButtons
			selectedCategory={selectedReport}
			onClickFirstButton={() => {
				setSelectedReport(donationsListLabel);
				refreshTable();
			}}
			onClickSecondButton={() => {
				console.log('click second');
				setSelectedReport(vouchersListLabel);
			}}
		/>
	);

	const getTableProps: any = {
		columns: headers,
		options: {
			paging: true,
			defaultExpanded: false,
			headerStyle: {
				height: '50px',
				padding: '0px 36px 0px 24px',
			},
		},
		components: {
			Groupbar: () => <></>,
		},
	};

	const EntityActions: IOverrideEntityActions<IDonationReportDetailsModel, IDonationReportDetailsModel> = {
		api: ReportServiceAPI,
		presetDataCallback: (data: any) => {
			return data;
		},
		getTableData: async function (query: Query<IDonationReportDetailsModel>) {
			const result: QueryResult<IDonationReportDetailsModel> = {
				data: [],
				page: 0,
				totalCount: 0,
			};
			const request: ISearchBaseDto = {
				itemsPerPage: query.pageSize,
				pageNumber: query.page ? query.page + 1 : 1,
				searchPhrase: query.search,
				sortColumn: '',
				sortDirection: '',
			};

			if (query.orderBy) request.sortColumn = query.orderBy.field as string;

			if (query.orderDirection) request.sortDirection = query.orderDirection;

			try {
				if (!id || selectedReport === vouchersListLabel) return result;
				else {
					const requestFilters: IGenericDonationReportModel = {
						locationId: id,
						startTime: filters.startTime,
						endTime: filters.endTime,
					};

					let response;

					if (filters.reportType === DictReportType.RealTime) {
						response = await ReportServiceAPI.GetDonationReportForLocation(request, requestFilters);
					} else if (filters.reportType === DictReportType.Daily) {
						response = await ReportServiceAPI.GetDonationReportDaily(request, requestFilters);
					} else {
						response = await ReportServiceAPI.GetDonationReportMonthly(request, requestFilters);
					}

					if (response.success) {
						result.data = response.data.items;
						result.page = response.data.currentPage - 1;
						result.totalCount = response.data.totalItemsCount;

						return result;
					} else {
						let message = t(`errorCodes.${response.errors[0].errorCode}`);
						enqueueSnackbar(`${t('common.errorOccured')}: ${message}`, {
							variant: 'error',
						});
					}
				}
			} catch (e) {
				errorDialog(e.message);
			}
			return result;
		},
	};

	const getRadioItem = (type: DictReportType, componentName: string) => {
		return (
			<>
				<FormControl key={type}>
					<FormControlLabel control={<Radio value={type} color='primary' name={type} />} label={componentName} />
				</FormControl>
			</>
		);
	};

	const renderInputFrom = (props: TextFieldProps): any => (
		<TextField
			type='text'
			onClick={props.onClick}
			value={props.value}
			onChange={props.onChange}
			InputProps={{
				startAdornment: (
					<InputAdornment position='start'>
						<p style={{ color: '#999999' }}>{t('common.form.from')}</p>
					</InputAdornment>
				),
				endAdornment: (
					<InputAdornment position='end'>
						<ArrowDownIcon />
					</InputAdornment>
				),
			}}
			style={{ marginRight: '30px' }}
		></TextField>
	);

	const renderInputTo = (props: TextFieldProps): any => (
		<TextField
			type='text'
			onClick={props.onClick}
			value={props.value}
			onChange={props.onChange}
			InputProps={{
				startAdornment: (
					<InputAdornment position='start'>
						<p style={{ color: '#999999' }}>{t('common.form.to')}</p>
					</InputAdornment>
				),
				endAdornment: (
					<InputAdornment position='end'>
						<ArrowDownIcon />
					</InputAdornment>
				),
			}}
			style={{ marginRight: '30px' }}
		></TextField>
	);

	const setLastDayOfNextMonth = (date: Date) => {
		return new Date(moment(date).subtract(1, 'months').add(1, 'months').endOf('month').format(yearMonthDayFormat));
	};

	const setFirstDayOfWeek = () => {
		return new Date(moment().subtract(1, 'weeks').startOf('isoWeek').format(yearMonthDayFormat));
	};

	const setLastDayOfWeek = () => {
		return new Date(moment().subtract(1, 'weeks').endOf('isoWeek').format(yearMonthDayFormat));
	};

	const setFirstDayOfMonth = () => {
		return new Date(moment().subtract(1, 'months').startOf('month').format(yearMonthDayFormat));
	};

	const setLastDayOfMonth = () => {
		return new Date(moment().subtract(1, 'months').endOf('month').format(yearMonthDayFormat));
	};

	const setFirstDayOfNextMonth = (date: Date) => {
		return new Date(moment(date).subtract(1, 'months').add(1, 'months').startOf('month').format(yearMonthDayFormat));
	};

	const getFilters = () => {
		let allFunctionalities = [DictReportType.RealTime, DictReportType.Daily, DictReportType.Monthly];

		return (
			<>
				<Grid item container xs={12} direction='row' alignItems='center' style={{ margin: '-20px 15px 0 15px' }}>
					<p className={classes.filterLabel}>{t('report.reportType')}</p>
					<Grid item container sm={10} xs={8} direction='row' style={{ marginTop: '1px' }}>
						<RadioGroup
							value={filters.reportType}
							onChange={(e) => {
								if (e.target.value === DictReportType.Monthly) {
									const isSameMonth = moment(filters.startTime).isSame(new Date(), 'month');
									const isAfter = moment(filters.startTime).isAfter(new Date());
									const today = new Date();
									setFilters({
										...filters,
										reportType: e.target.value,
										startTime:
											isSameMonth || isAfter
												? new Date(today.getFullYear(), today.getMonth() - 1, 1)
												: new Date(filters.startTime!.getFullYear(), filters.startTime!.getMonth(), 1),
										endTime:
											isSameMonth || isAfter
												? new Date(moment(today).subtract(1, 'months').endOf('month').format(yearMonthDayFormat))
												: setLastDayOfNextMonth(filters.startTime!),
										dateMonthly:
											isSameMonth || isAfter
												? new Date(today.getFullYear(), today.getMonth() - 1)
												: filters.startTime!,
									});
								} else {
									setFilters({ ...filters, reportType: e.target.value });
								}
								refreshTable();
							}}
							row
						>
							{allFunctionalities.map((column, i) => getRadioItem(column, DictReportTypeLabel(t).get(column) ?? ''))}
						</RadioGroup>
					</Grid>
				</Grid>

				<div style={{ display: 'flex', margin: '15px', flexWrap: 'wrap' }}>
					{filters.reportType === DictReportType.Monthly ? (
						<MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
							<DatePicker
								views={['month']}
								name='dateFrom'
								format={dateFormatWithDots}
								id='dateFrom'
								value={filters.startTime}
								onChange={(e) => {
									if (e)
										setFilters({
											...filters,
											startTime: setFirstDayOfNextMonth(e),
											endTime: setLastDayOfNextMonth(e),
											dateMonthly: e,
										});
									refreshTable();
								}}
								label={t('report.Date')}
								maxDate={moment().toDate()}
							/>
						</MuiPickersUtilsProvider>
					) : (
						<>
							<MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
								<DatePicker
									name='dateFrom'
									format={dateFormatWithDots}
									id='dateFrom'
									value={filters.startTime}
									onChange={(e) => {
										let selectedDate = moment(e);
										let dateTo = filters.endTime;

										if (e! > filters.endTime!) {
											dateTo = new Date(selectedDate.add(30, 'days').format(yearMonthDayFormat));
										}

										setFilters({ ...filters, startTime: selectedDate.toDate(), endTime: dateTo, period: undefined });
										refreshTable();
									}}
									label={t('common.form.from')}
									maxDate={filters.endTime}
									TextFieldComponent={renderInputFrom}
								/>
							</MuiPickersUtilsProvider>

							<MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
								<DatePicker
									name='dateTo'
									format={dateFormatWithDots}
									id='dateTo'
									value={filters.endTime}
									onChange={(e) => {
										let endDate = new Date(moment(e).format(yearMonthDayFormat));
										let startDate = new Date(moment(filters.startTime).format(yearMonthDayFormat));
										let selectedDate = moment(e, yearMonthDayFormat);
										let dateFrom;

										if (endDate < startDate) {
											dateFrom = selectedDate.subtract(1, 'days').format(yearMonthDayFormat);
										} else {
											dateFrom = startDate;
										}
										setFilters({ ...filters, startTime: dateFrom, endTime: selectedDate.toDate(), period: undefined });
										refreshTable();
									}}
									label={t('common.form.to')}
									maxDate={moment().toDate()}
									minDate={filters.startTime}
									TextFieldComponent={renderInputTo}
								/>
							</MuiPickersUtilsProvider>
							<FormControl>
								<Select
									onChange={(e: any) => {
										let dateFrom = filters.startTime;
										let dateTo = filters.endTime;

										if (e.target.value === t('report.LastWeek')) {
											dateFrom = setFirstDayOfWeek();
											dateTo = setLastDayOfWeek();
										}

										if (e.target.value === t('report.LastMonth')) {
											dateFrom = setFirstDayOfMonth();
											dateTo = setLastDayOfMonth();
										}

										setFilters({
											...filters,
											startTime: dateFrom,
											endTime: dateTo,
											period: e.target.value,
										});
										refreshTable();
									}}
									style={{ width: '300px' }}
									value={filters.period}
									displayEmpty={true}
									renderValue={(value: any) => (value ? value : t('report.ChooseTimePeriod'))}
								>
									<MenuItem value={t('report.LastWeek')}>{t('report.LastWeek')}</MenuItem>
									<MenuItem value={t('report.LastMonth')}>{t('report.LastMonth')}</MenuItem>
								</Select>
							</FormControl>
						</>
					)}
				</div>
			</>
		);
	};

	const customFilters = React.useMemo(
		() => <>{getFilters()}</>,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[filters, selectedReport]
	);

	let entityPageProps = {
		tableRef: tableRef,
		EntityActions: EntityActions,
		headers: headers,
		pageTitle: '',
		tableProps: getTableProps,
		isDetails: props.isInDetails,
		titleElement: entityTitleButtons(),
		disableAddNew: true,
		defaultMaxContent: true,
		disableSearchBar: true,
		customFilters: customFilters,
	};

	function refreshTable() {
		selectedReport === donationsListLabel && tableRef.current && (tableRef.current! as any).onQueryChange();
	}

	return (
		<>
			{selectedReport === donationsListLabel ? (
				<EntityPage {...entityPageProps} />
			) : (
				<div style={{ margin: '45px 15px' }}>{entityTitleButtons()}</div>
			)}
		</>
	);
};
